import { useState, useEffect, useCallback, useMemo } from 'react';
import { previousFriday } from 'date-fns';
import { toLocalDateStr } from '../lib/utils';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  AreaChart, Area, PieChart, Pie, Cell
} from 'recharts';
import { BrowserLinearService } from '@/services/linear-service';
import type { LinearActivityEvent } from '@/services/types';
import { PlatformIndicators, normalizeLinearEvents } from './PlatformIndicators';
import { LinearIssueDetail } from './LinearIssueDetail';
import { WaterfallView, type WaterfallEvent } from './WaterfallView';
import { notifyAnalysisComplete } from '@/services/notifications';
import { NotificationPermissionDialog } from './NotificationPermissionDialog';
import * as historyStore from '@/services/analysis-history-store';
import * as audit from '@/services/audit';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faArrowRightArrowLeft,
  faArrowTrendUp,
  faCalendarDays,
  faChartPie,
  faChevronDown,
  faChevronRight,
  faCircleCheck,
  faCircleExclamation,
  faEye,
  faFlag,
  faMagnifyingGlass,
  faPlus,
  faRotate,
  faSpinner,
  faUsers,
  faXmark
} from '@fortawesome/free-solid-svg-icons';

const mkIcon = (def: import('@fortawesome/fontawesome-svg-core').IconDefinition) =>
  ({ className = '' }: { className?: string }) =>
    <FontAwesomeIcon icon={def} className={className} />;

export interface LinearReviewData {
  events: LinearActivityEvent[];
  selectedTeam: string;
  teamName: string;
  lookbackDays: number;
}

interface LinearActivityViewProps {
  onClose: () => void;
  apiKey: string;
  inline?: boolean;
  reviewData?: LinearReviewData;
}

const EVENT_TYPE_CONFIG: Record<string, { icon: React.ComponentType<{ className?: string }>; color: string; label: string }> = {
  status_change: { icon: mkIcon(faArrowRightArrowLeft), color: 'text-blue-400', label: 'Status Change' },
  assignment: { icon: mkIcon(faUsers), color: 'text-purple-400', label: 'Assignment' },
  created: { icon: mkIcon(faPlus), color: 'text-emerald-400', label: 'Created' },
  completed: { icon: mkIcon(faCircleCheck), color: 'text-green-400', label: 'Completed' },
  field_change: { icon: mkIcon(faEye), color: 'text-yellow-400', label: 'Field Change' },
};

function getStatusColor(status: string): string {
  const s = status.toLowerCase();
  if (s.includes('done') || s.includes('completed')) return 'bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 border-emerald-500/30';
  if (s.includes('in progress') || s.includes('started')) return 'bg-blue-500/20 text-blue-700 dark:text-blue-300 border-blue-500/30';
  if (s.includes('backlog') || s.includes('triage')) return 'bg-slate-500/20 text-slate-700 dark:text-slate-300 border-slate-500/30';
  if (s.includes('cancelled') || s.includes('canceled')) return 'bg-red-500/20 text-red-700 dark:text-red-300 border-red-500/30';
  return 'bg-slate-500/20 text-slate-700 dark:text-slate-300 border-slate-500/30';
}

export function LinearActivityView({ onClose, apiKey, inline, reviewData }: LinearActivityViewProps) {
  const [events, setEvents] = useState<LinearActivityEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [teams, setTeams] = useState<{ id: string; name: string; key: string }[]>([]);
  const [selectedTeam, setSelectedTeam] = useState('');
  const [loadingTeams, setLoadingTeams] = useState(false);
  const [lookbackDays, setLookbackDays] = useState(14);
  const [dateRangePreset, setDateRangePreset] = useState<number | 'friday-monday'>(14);

  // Filters
  const [filterSearch, setFilterSearch] = useState('');
  const [filterAuthor, setFilterAuthor] = useState('');
  const [expandedDates, setExpandedDates] = useState<Set<string>>(new Set());
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const [showCharts, setShowCharts] = useState(true);
  const [isDark, setIsDark] = useState(() => document.documentElement.classList.contains('dark'));

  useEffect(() => {
    const obs = new MutationObserver(() => setIsDark(document.documentElement.classList.contains('dark')));
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => obs.disconnect();
  }, []);

  const service = useMemo(() => new BrowserLinearService({ apiKey }), [apiKey]);

  const [isReview, setIsReview] = useState(false);

  // Hydrate from reviewData (past analysis reopen)
  useEffect(() => {
    if (!reviewData) return;
    setEvents(reviewData.events);
    setSelectedTeam(reviewData.selectedTeam);
    setLookbackDays(reviewData.lookbackDays);
    setIsReview(true);
    const today = toLocalDateStr();
    const yesterday = toLocalDateStr(new Date(Date.now() - 86400000));
    setExpandedDates(new Set([today, yesterday]));
  }, [reviewData]);

  useEffect(() => {
    if (reviewData) return;
    loadTeams();
  }, []);

  const loadTeams = async () => {
    setLoadingTeams(true);
    try {
      const t = await service.getTeams();
      setTeams(t);
      if (t.length > 0) setSelectedTeam(t[0].id);
    } catch (err: any) {
      console.error('Failed to load Linear teams:', err);
      setError('Failed to load teams. Check your API key.');
    } finally {
      setLoadingTeams(false);
    }
  };

  const fetchActivity = useCallback(async () => {
    if (!selectedTeam) return;
    setLoading(true);
    setError(null);
    const startTime = Date.now();
    audit.auditAnalysisStarted('linear', { team: selectedTeam });
    try {
      const data = await service.getRecentActivity(selectedTeam, lookbackDays);
      setEvents(data);
      const today = toLocalDateStr();
      const yesterday = toLocalDateStr(new Date(Date.now() - 86400000));
      setExpandedDates(new Set([today, yesterday]));
      const durationMs = Date.now() - startTime;
      audit.auditAnalysisCompleted('linear', durationMs, { team: selectedTeam, eventCount: data.length });
      notifyAnalysisComplete({ mode: 'linear', commitCount: 0, eventCount: data.length, durationMs, success: true });

      // Save to analysis history
      const teamName = teams.find(t => t.id === selectedTeam)?.name || selectedTeam;
      const snapId = historyStore.generateSnapshotId();
      historyStore.saveSnapshot(
        {
          id: snapId,
          mode: 'linear',
          title: `Linear — ${teamName}`,
          timestamp: new Date().toISOString(),
          durationMs,
          success: true,
          summary: { commitCount: 0, eventCount: data.length, authorCount: 0 },
        },
        { events: data, selectedTeam, teamName, lookbackDays } satisfies LinearReviewData
      ).catch(err => console.error('[analysis-history] Failed to save linear snapshot:', err));
    } catch (err: any) {
      setError(err.message || 'Failed to fetch Linear activity');
      audit.auditAnalysisFailed('linear', err.message, { team: selectedTeam });
      notifyAnalysisComplete({ mode: 'linear', commitCount: 0, eventCount: 0, durationMs: Date.now() - startTime, success: false, error: err.message });
    } finally {
      setLoading(false);
    }
  }, [selectedTeam, lookbackDays, service]);

  useEffect(() => {
    if (selectedTeam) fetchActivity();
  }, [selectedTeam, fetchActivity]);

  const filteredEvents = useMemo(() => {
    const yesterdayStr = toLocalDateStr(new Date(Date.now() - 86400000));
    return events.filter((e) => {
      // "Yesterday" preset: only show events from calendar yesterday
      if (dateRangePreset === -1) {
        const eventDate = toLocalDateStr(new Date(e.timestamp));
        if (eventDate !== yesterdayStr) return false;
      }
      if (filterAuthor && e.author.displayName !== filterAuthor) return false;
      if (filterSearch) {
        const q = filterSearch.toLowerCase();
        if (!e.issueSummary.toLowerCase().includes(q) &&
            !e.issueIdentifier.toLowerCase().includes(q) &&
            !e.author.displayName.toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [events, filterAuthor, filterSearch, dateRangePreset]);

  const groupedByDate = useMemo(() => {
    const groups = new Map<string, LinearActivityEvent[]>();
    for (const event of filteredEvents) {
      const date = toLocalDateStr(new Date(event.timestamp));
      if (!groups.has(date)) groups.set(date, []);
      groups.get(date)!.push(event);
    }
    return groups;
  }, [filteredEvents]);

  const uniqueAuthors = useMemo(() => {
    const set = new Set(events.map((e) => e.author.displayName));
    return [...set].sort();
  }, [events]);

  // ==================== CHART DATA ====================
  const CHART_COLORS = ['#6366f1', '#10b981', '#f59e0b', '#a855f7', '#f43f5e', '#06b6d4', '#f97316', '#ec4899', '#3b82f6', '#14b8a6'];

  const dailyData = useMemo(() => {
    const dayMap = new Map<string, { date: string; _ts: number; events: number }>();
    for (const e of filteredEvents) {
      const d = new Date(e.timestamp);
      const key = toLocalDateStr(d);
      if (!dayMap.has(key)) dayMap.set(key, { date: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }), _ts: d.setHours(0, 0, 0, 0), events: 0 });
      dayMap.get(key)!.events++;
    }
    return Array.from(dayMap.values()).sort((a, b) => a._ts - b._ts).map(({ _ts, ...rest }) => rest);
  }, [filteredEvents]);

  const authorChartData = useMemo(() => {
    const counts = new Map<string, number>();
    for (const e of filteredEvents) {
      const name = e.author.displayName;
      counts.set(name, (counts.get(name) || 0) + 1);
    }
    return Array.from(counts.entries())
      .map(([name, count]) => ({ name: name.length > 12 ? name.split(' ')[0] : name, fullName: name, issues: count }))
      .sort((a, b) => b.issues - a.issues);
  }, [filteredEvents]);

  const eventTypeChartData = useMemo(() => {
    const counts = new Map<string, number>();
    for (const e of filteredEvents) { counts.set(e.type, (counts.get(e.type) || 0) + 1); }
    return Array.from(counts.entries())
      .map(([type, count], i) => ({ name: EVENT_TYPE_CONFIG[type]?.label || type, count, fill: CHART_COLORS[i % CHART_COLORS.length] }))
      .sort((a, b) => b.count - a.count);
  }, [filteredEvents]);

  const statusChartData = useMemo(() => {
    const counts = new Map<string, number>();
    for (const e of filteredEvents) {
      if (e.toState) counts.set(e.toState, (counts.get(e.toState) || 0) + 1);
    }
    return Array.from(counts.entries())
      .map(([name, count], i) => ({ name, count, fill: CHART_COLORS[i % CHART_COLORS.length] }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);
  }, [filteredEvents]);

  const waterfallEvents: WaterfallEvent[] = useMemo(() => {
    return filteredEvents.map(e => ({
      id: e.id,
      timestamp: e.timestamp,
      issueKey: e.issueIdentifier,
      issueSummary: e.issueSummary,
      type: e.type,
      author: e.author.displayName,
      platform: 'linear' as const,
      detail: e.fromState && e.toState ? `${e.fromState} → ${e.toState}` : e.toAssignee ? `→ ${e.toAssignee}` : undefined,
    }));
  }, [filteredEvents]);

  const toggleDate = (date: string) => {
    setExpandedDates((prev) => {
      const next = new Set(prev);
      if (next.has(date)) next.delete(date);
      else next.add(date);
      return next;
    });
  };

  const selectedTeamName = teams.find(t => t.id === selectedTeam);

  return (
    <div className={`${inline ? 'min-h-full overflow-y-auto' : 'fixed inset-0 z-50 bg-white dark:bg-slate-950 text-gray-900 dark:text-white overflow-hidden flex flex-col'}`}>
      <NotificationPermissionDialog isDark={isDark} />
      {isReview && (
        <div className={`px-4 py-2 text-xs font-medium text-center ${isDark ? 'bg-amber-500/10 text-amber-300 border-b border-amber-500/20' : 'bg-amber-50 text-amber-700 border-b border-amber-200'}`}>
          Reviewing past analysis — data is read-only
        </div>
      )}
      {/* Header - hidden when inline (uses main app header) */}
      {!inline && (
        <div className="sticky top-0 z-10 bg-white/80 dark:bg-slate-900/80 backdrop-blur-md border-b border-gray-200 dark:border-slate-700/50">
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button onClick={onClose} className="p-2 hover:bg-gray-100 dark:hover:bg-slate-800 rounded-lg transition-colors">
                <FontAwesomeIcon icon={faArrowLeft} className="h-5 w-5 text-gray-500 dark:text-slate-400" />
              </button>
              <img src="/brands/linear/dark.svg" className="h-5 w-5 dark:hidden" alt="" />
              <img src="/brands/linear/light.svg" className="h-5 w-5 hidden dark:block" alt="" />
              <div>
                <h1 className="text-lg font-semibold text-gray-900 dark:text-white">Linear Activity</h1>
                <p className="text-xs text-gray-500 dark:text-slate-400">Activity timeline & indicators</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {events.length > 0 && <span className="text-xs text-gray-400 dark:text-slate-500">{filteredEvents.length} events</span>}
              <button onClick={onClose} className="p-2 hover:bg-gray-100 dark:hover:bg-slate-800 rounded-lg transition-colors">
                <FontAwesomeIcon icon={faXmark} className="h-5 w-5 text-gray-500 dark:text-slate-400" />
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-6 py-6 space-y-6">
        {/* Config Panel — always visible */}
        <div className="bg-white/90 dark:bg-slate-800/50 border border-gray-200 dark:border-slate-700/50 rounded-xl p-5">
          <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
            {/* Team selector */}
            <div className="md:col-span-2">
              <label className="flex items-center gap-1.5 text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wide mb-2">
                <FontAwesomeIcon icon={faUsers} className="h-3.5 w-3.5" /> Team
              </label>
              {loadingTeams ? (
                <div className="flex items-center gap-2 text-xs text-gray-400 dark:text-slate-500 py-2.5"><FontAwesomeIcon icon={faSpinner} className="h-3 w-3 animate-spin" /> Loading teams...</div>
              ) : teams.length === 0 ? (
                <div className="text-xs text-gray-400 dark:text-slate-500 py-2.5">No teams found</div>
              ) : (
                <select
                  value={selectedTeam}
                  onChange={(e) => setSelectedTeam(e.target.value)}
                  className="w-full px-3 py-2.5 bg-gray-100 dark:bg-slate-700/30 border border-gray-300 dark:border-slate-600 rounded-lg text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/30 transition-all"
                >
                  <option value="">Select team...</option>
                  {teams.map((t) => (
                    <option key={t.id} value={t.id}>{t.name} ({t.key})</option>
                  ))}
                </select>
              )}
            </div>

            {/* Time Range */}
            <div className="md:col-span-4">
              <label className="flex items-center gap-1.5 text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wide mb-2">
                <FontAwesomeIcon icon={faCalendarDays} className="h-3.5 w-3.5" /> Time Range
              </label>
              <div className="flex flex-wrap gap-1.5 mb-2">
                {[
                  {
                    label: 'Friday -> Monday (now)',
                    getValue: () => {
                      const now = new Date();
                      const days = Math.ceil((now.getTime() - previousFriday(now).getTime()) / (1000 * 60 * 60 * 24));
                      return days;
                    }
                  },
                  { label: 'Yesterday', days: -1 },
                  { label: '24h', days: 1 },
                  { label: '7d', days: 7 },
                  { label: '2w', days: 14 },
                  { label: '30d', days: 30 },
                  { label: '60d', days: 60 },
                  { label: '90d', days: 90 },
                  { label: 'All Time', days: -2 },
                ].map(preset => (
                  <button
                    key={preset.label}
                    onClick={() => {
                      setDateRangePreset(preset.getValue ? 'friday-monday' : (preset.days || 0));
                      if (preset.days === -1) {
                        setLookbackDays(1); // Yesterday handled in fetchActivity
                      } else if (preset.days === -2) {
                        setLookbackDays(3650); // ~10 years
                      } else if (preset.getValue) {
                        setLookbackDays(preset.getValue());
                      } else {
                        setLookbackDays(preset.days);
                      }
                    }}
                    className={`px-2.5 py-1.5 text-xs font-medium rounded-lg transition-colors ${(preset.getValue ? dateRangePreset === 'friday-monday' : dateRangePreset === preset.days)
                        ? 'bg-indigo-500/20 text-indigo-600 dark:text-indigo-400 border border-indigo-500/30'
                        : 'bg-gray-100 dark:bg-slate-700/30 text-gray-500 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-200 dark:hover:bg-slate-700/50 border border-transparent'
                      }`}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
              <div className="text-[10px] text-gray-400 dark:text-slate-500">
                {selectedTeamName ? `${selectedTeamName.name} — ` : ''}Last {lookbackDays} day{lookbackDays !== 1 ? 's' : ''}
              </div>
            </div>

            {/* Search */}
            <div className="md:col-span-2">
              <label className="flex items-center gap-1.5 text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wide mb-2">
                <FontAwesomeIcon icon={faMagnifyingGlass} className="h-3.5 w-3.5" /> Search
              </label>
              <input
                type="text"
                value={filterSearch}
                onChange={(e) => setFilterSearch(e.target.value)}
                placeholder="Search issues..."
                className="w-full px-3 py-2.5 bg-gray-100 dark:bg-slate-700/30 border border-gray-300 dark:border-slate-600 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/30 transition-all"
              />
            </div>

            {/* Author filter */}
            <div className="md:col-span-2">
              <label className="flex items-center gap-1.5 text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wide mb-2">
                <FontAwesomeIcon icon={faUsers} className="h-3.5 w-3.5" /> Author
              </label>
              <select
                value={filterAuthor}
                onChange={(e) => setFilterAuthor(e.target.value)}
                className="w-full px-3 py-2.5 bg-gray-100 dark:bg-slate-700/30 border border-gray-300 dark:border-slate-600 rounded-lg text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/30 transition-all"
              >
                <option value="">All authors</option>
                {uniqueAuthors.map((a) => (
                  <option key={a} value={a}>{a}</option>
                ))}
              </select>
            </div>

            {/* Fetch button */}
            <div className="md:col-span-2 flex items-end gap-2">
              <button
                onClick={fetchActivity}
                disabled={loading || !selectedTeam}
                className="flex-1 px-4 py-2.5 bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-medium rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
              >
                {loading ? (<><FontAwesomeIcon icon={faSpinner} className="h-4 w-4 animate-spin" /> Loading...</>) : (<><FontAwesomeIcon icon={faRotate} className="h-4 w-4" /> Fetch Activity</>)}
              </button>
            </div>
          </div>
        </div>

        {/* Beta notice */}
        <div className="flex items-start gap-2.5 p-3 bg-white/50 dark:bg-white/[0.04] backdrop-blur-md border border-black/[0.06] dark:border-white/[0.08] rounded-xl">
          <FontAwesomeIcon icon={faCircleExclamation} className="h-4 w-4 text-indigo-500 dark:text-indigo-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-xs font-medium text-gray-700 dark:text-slate-200">Beta Feature</p>
            <p className="text-xs text-gray-500 dark:text-slate-400 mt-0.5">Linear integration is in beta and may be prone to errors or incomplete data.</p>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700/50 rounded-xl flex items-center gap-3">
            <FontAwesomeIcon icon={faCircleExclamation} className="h-5 w-5 text-red-500 dark:text-red-400 flex-shrink-0" />
            <p className="text-sm text-red-600 dark:text-red-300">{error}</p>
          </div>
        )}

        {/* Loading state */}
        {loading && events.length === 0 && (
          <div className="flex items-center justify-center py-20">
            <FontAwesomeIcon icon={faSpinner} className="h-8 w-8 animate-spin text-indigo-400" />
          </div>
        )}

        {/* Empty state */}
        {!loading && events.length === 0 && !error && (
          <div className="text-center py-20 text-gray-400 dark:text-slate-500">
            <FontAwesomeIcon icon={faCalendarDays} className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p>No activity found. Select a team and refresh.</p>
          </div>
        )}

        {/* Lead & Lag Indicators */}
        {events.length > 0 && (
          <div className="bg-white/90 dark:bg-slate-800/50 border border-gray-200 dark:border-slate-700/50 rounded-xl overflow-hidden">
            <PlatformIndicators events={normalizeLinearEvents(events)} platform="linear" />
          </div>
        )}

        {/* Charts & Analytics */}
        {filteredEvents.length > 0 && (
          <div className="bg-white/90 dark:bg-slate-800/50 border border-gray-200 dark:border-slate-700/50 rounded-xl overflow-hidden">
            <button
              onClick={() => setShowCharts(v => !v)}
              className="w-full flex items-center justify-between px-5 py-3 hover:bg-gray-50 dark:hover:bg-slate-700/20 transition-colors"
            >
              <div className="flex items-center gap-2">
                <FontAwesomeIcon icon={faArrowTrendUp} className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
                <span className="text-sm font-medium text-gray-900 dark:text-white">Charts & Analytics</span>
                <span className="text-[10px] text-gray-400 dark:text-slate-500">({filteredEvents.length} events)</span>
              </div>
              <FontAwesomeIcon icon={faChevronDown} className={`h-4 w-4 text-gray-400 dark:text-slate-400 transition-transform ${showCharts ? 'rotate-180' : ''}`} />
            </button>
            {showCharts && (
              <div className="px-5 pb-5 grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4 items-start">
                {/* Daily Activity */}
                <div className="bg-gray-50 dark:bg-slate-900/50 rounded-xl border border-gray-200 dark:border-slate-700/30 p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <FontAwesomeIcon icon={faArrowTrendUp} className="h-3.5 w-3.5 text-indigo-600 dark:text-indigo-400" />
                    <h3 className="text-xs font-semibold text-gray-900 dark:text-white">Daily Activity</h3>
                  </div>
                  <ResponsiveContainer width="100%" height={180}>
                    <AreaChart data={dailyData}>
                      <CartesianGrid strokeDasharray="3 3" stroke={isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.1)'} />
                      <XAxis dataKey="date" tick={{ fontSize: 10, fill: isDark ? 'rgba(255,255,255,0.4)' : '#6b7280' }} />
                      <YAxis tick={{ fontSize: 10, fill: isDark ? 'rgba(255,255,255,0.4)' : '#6b7280' }} />
                      <Tooltip contentStyle={{ backgroundColor: isDark ? '#1e293b' : '#fff', border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : '#e5e7eb'}`, borderRadius: '8px', fontSize: '12px', color: isDark ? '#fff' : '#111' }} />
                      <Area type="monotone" dataKey="events" name="Events" stroke="#6366f1" fill="#6366f1" fillOpacity={0.3} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>

                {/* Events by Author */}
                <div className="bg-gray-50 dark:bg-slate-900/50 rounded-xl border border-gray-200 dark:border-slate-700/30 p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <FontAwesomeIcon icon={faUsers} className="h-3.5 w-3.5 text-purple-400" />
                    <h3 className="text-xs font-semibold text-gray-900 dark:text-white">Activity by Author</h3>
                  </div>
                  <div className="overflow-y-auto max-h-[180px]">
                    <div style={{ height: Math.max(180, authorChartData.length * 32) }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={authorChartData} layout="vertical">
                          <CartesianGrid strokeDasharray="3 3" stroke={isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.1)'} />
                          <XAxis type="number" tick={{ fontSize: 10, fill: isDark ? 'rgba(255,255,255,0.4)' : '#6b7280' }} />
                          <YAxis dataKey="name" type="category" tick={{ fontSize: 10, fill: isDark ? 'rgba(255,255,255,0.4)' : '#6b7280' }} width={80} />
                          <Tooltip contentStyle={{ backgroundColor: isDark ? '#1e293b' : '#fff', border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : '#e5e7eb'}`, borderRadius: '8px', fontSize: '12px', color: isDark ? '#fff' : '#111' }}
                            labelFormatter={(label: string) => { const item = authorChartData.find(d => d.name === label); return item?.fullName || label; }} />
                          <Bar dataKey="issues" name="Events" fill="#a855f7" radius={[0, 4, 4, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>

                {/* Event Type Distribution */}
                <div className="bg-gray-50 dark:bg-slate-900/50 rounded-xl border border-gray-200 dark:border-slate-700/30 p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <FontAwesomeIcon icon={faChartPie} className="h-3.5 w-3.5 text-amber-400" />
                    <h3 className="text-xs font-semibold text-gray-900 dark:text-white">Event Type Breakdown</h3>
                  </div>
                  <ResponsiveContainer width="100%" height={180}>
                    <PieChart>
                      <Pie data={eventTypeChartData} dataKey="count" nameKey="name" cx="50%" cy="50%" outerRadius={65} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={false} style={{ fontSize: '9px' }}>
                        {eventTypeChartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.fill} />
                        ))}
                      </Pie>
                      <Tooltip contentStyle={{ backgroundColor: isDark ? '#1e293b' : '#fff', border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : '#e5e7eb'}`, borderRadius: '8px', fontSize: '12px', color: isDark ? '#fff' : '#111' }} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>

                {/* Status Transitions */}
                {statusChartData.length > 0 && (
                  <div className="bg-gray-50 dark:bg-slate-900/50 rounded-xl border border-gray-200 dark:border-slate-700/30 p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <FontAwesomeIcon icon={faFlag} className="h-3.5 w-3.5 text-cyan-400" />
                      <h3 className="text-xs font-semibold text-gray-900 dark:text-white">Status Transitions</h3>
                    </div>
                    <ResponsiveContainer width="100%" height={180}>
                      <BarChart data={statusChartData}>
                        <CartesianGrid strokeDasharray="3 3" stroke={isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.1)'} />
                        <XAxis dataKey="name" tick={{ fontSize: 9, fill: isDark ? 'rgba(255,255,255,0.4)' : '#6b7280' }} interval={0} angle={-30} textAnchor="end" height={50} />
                        <YAxis tick={{ fontSize: 10, fill: isDark ? 'rgba(255,255,255,0.4)' : '#6b7280' }} />
                        <Tooltip contentStyle={{ backgroundColor: isDark ? '#1e293b' : '#fff', border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : '#e5e7eb'}`, borderRadius: '8px', fontSize: '12px', color: isDark ? '#fff' : '#111' }} />
                        <Bar dataKey="count" name="Transitions" radius={[4, 4, 0, 0]}>
                          {statusChartData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.fill} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Waterfall Timeline */}
        {waterfallEvents.length > 0 && (
          <WaterfallView
            events={waterfallEvents}
            title="Linear Waterfall Timeline"
            onEventClick={(evt) => {
              const match = filteredEvents.find(e => e.issueIdentifier === evt.issueKey);
              if (!match) return;
              // Auto-expand the date group containing this event
              const date = toLocalDateStr(new Date(match.timestamp));
              setExpandedDates(prev => {
                const next = new Set(prev);
                next.add(date);
                return next;
              });
              setSelectedEventId(match.id);
              setTimeout(() => {
                const el = document.getElementById(`linear-event-${match.id}`);
                el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }, 150);
            }}
          />
        )}

        {/* Timeline grouped by date */}
        {[...groupedByDate.entries()].map(([date, dateEvents]) => {
          const isExpanded = expandedDates.has(date);
          const isToday = date === toLocalDateStr();
          const isYesterday = date === toLocalDateStr(new Date(Date.now() - 86400000));
          const label = isToday ? 'Today' : isYesterday ? 'Yesterday' : new Date(date + 'T00:00:00').toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' });

          return (
            <div key={date} className="mb-2">
              <button
                onClick={() => toggleDate(date)}
                className="w-full flex items-center gap-3 px-4 py-3 bg-white/60 dark:bg-slate-800/50 hover:bg-gray-50 dark:hover:bg-slate-800 rounded-lg transition-colors border border-gray-200 dark:border-slate-700/30"
              >
                {isExpanded ? <FontAwesomeIcon icon={faChevronDown} className="h-4 w-4 text-gray-400 dark:text-slate-400" /> : <FontAwesomeIcon icon={faChevronRight} className="h-4 w-4 text-gray-400 dark:text-slate-400" />}
                <span className="text-sm font-semibold text-gray-900 dark:text-white">{label}</span>
                <span className="text-xs text-gray-400 dark:text-slate-500">{dateEvents.length} events</span>
              </button>

              {isExpanded && (
                <div className="ml-4 border-l-2 border-gray-200 dark:border-slate-800 pl-4 py-2 space-y-2">
                  {dateEvents.map((event) => {
                    const config = EVENT_TYPE_CONFIG[event.type] || EVENT_TYPE_CONFIG.field_change;
                    const Icon = config.icon;
                    const isSelected = selectedEventId === event.id;

                    return (
                      <div key={event.id} id={`linear-event-${event.id}`}>
                        <div
                          onClick={() => setSelectedEventId(isSelected ? null : event.id)}
                          className={`flex items-start gap-3 p-3 bg-gray-50 dark:bg-slate-900/50 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-800/50 transition-colors cursor-pointer ${isSelected ? 'bg-indigo-500/5 dark:bg-indigo-500/5' : ''}`}
                        >
                          <div className={`mt-0.5 flex-shrink-0 flex items-center gap-1 ${config.color}`}>
                            <FontAwesomeIcon icon={faChevronRight} className={`h-3 w-3 text-gray-500 dark:text-slate-600 transition-transform ${isSelected ? 'rotate-90 text-indigo-400' : ''}`} />
                            <Icon className="h-4 w-4" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-xs font-mono text-indigo-600 dark:text-indigo-400 bg-indigo-500/10 px-1.5 py-0.5 rounded">
                                {event.issueIdentifier}
                              </span>
                              <span className="text-sm text-gray-900 dark:text-white truncate">{event.issueSummary}</span>
                            </div>
                            <div className="flex items-center gap-2 mt-1 text-xs text-gray-500 dark:text-slate-400">
                              <span>{event.author.displayName}</span>
                              <span>•</span>
                              <span>{new Date(event.timestamp).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' })}</span>
                              {event.fromState && event.toState && (
                                <>
                                  <span>•</span>
                                  <span className={getStatusColor(event.fromState) + ' px-1.5 py-0.5 rounded text-[10px] border'}>{event.fromState}</span>
                                  <span>→</span>
                                  <span className={getStatusColor(event.toState) + ' px-1.5 py-0.5 rounded text-[10px] border'}>{event.toState}</span>
                                </>
                              )}
                              {event.fromAssignee && event.toAssignee && (
                                <>
                                  <span>•</span>
                                  <span className="text-gray-400 dark:text-slate-500">{event.fromAssignee}</span>
                                  <span>→</span>
                                  <span className="text-gray-700 dark:text-slate-300">{event.toAssignee}</span>
                                </>
                              )}
                              {event.toAssignee && !event.fromAssignee && (
                                <>
                                  <span>•</span>
                                  <span>→ {event.toAssignee}</span>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                        {/* Inline issue detail accordion */}
                        {isSelected && (
                          <LinearIssueDetail
                            issueIdentifier={event.issueIdentifier}
                            apiKey={apiKey}
                            onClose={() => setSelectedEventId(null)}
                            onNavigate={(id) => { setSelectedEventId(null); setTimeout(() => setSelectedEventId('nav-' + id), 50); }}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
